// src/app.ts
import { useCallback, useEffect, useState } from "react";

// src/types.ts
var ALLOWED_BRIDGE_VERSIONS = ["1.0", "2.0"];
var BRIDGE_VERSION = "2.0";
var ParentType = /* @__PURE__ */ ((ParentType2) => {
  ParentType2["WORKSPACE"] = "workspace";
  ParentType2["ORGANIZATION"] = "organization";
  return ParentType2;
})(ParentType || {});
var WSEventType = /* @__PURE__ */ ((WSEventType2) => {
  WSEventType2["WS_HELLO"] = "WS_HELLO";
  WSEventType2["WS_CONTEXT_UPDATE"] = "WS_CONTEXT_UPDATE";
  WSEventType2["WS_LANGUAGE_CHANGED"] = "WS_LANGUAGE_CHANGED";
  WSEventType2["WS_WORKSPACE_CHANGED"] = "WS_WORKSPACE_CHANGED";
  WSEventType2["WS_ORG_CHANGED"] = "WS_ORG_CHANGED";
  WSEventType2["WS_SESSION_EXPIRED"] = "WS_SESSION_EXPIRED";
  WSEventType2["WS_APP_NAVIGATE"] = "WS_APP_NAVIGATE";
  WSEventType2["WS_CUSTOM_EVENT"] = "WS_CUSTOM_EVENT";
  WSEventType2["ORG_HELLO"] = "ORG_HELLO";
  WSEventType2["ORG_CONTEXT_UPDATE"] = "ORG_CONTEXT_UPDATE";
  WSEventType2["ORG_LANGUAGE_CHANGED"] = "ORG_LANGUAGE_CHANGED";
  WSEventType2["ORG_WORKSPACE_CHANGED"] = "ORG_WORKSPACE_CHANGED";
  WSEventType2["ORG_SESSION_EXPIRED"] = "ORG_SESSION_EXPIRED";
  WSEventType2["ORG_APP_NAVIGATE"] = "ORG_APP_NAVIGATE";
  WSEventType2["ORG_CUSTOM_EVENT"] = "ORG_CUSTOM_EVENT";
  WSEventType2["APP_HELLO"] = "APP_HELLO";
  WSEventType2["APP_READY"] = "APP_READY";
  WSEventType2["APP_CONTEXT_REQUEST"] = "APP_CONTEXT_REQUEST";
  WSEventType2["APP_SHOW_TOAST"] = "APP_SHOW_TOAST";
  WSEventType2["APP_NAVIGATE"] = "APP_NAVIGATE";
  WSEventType2["APP_LOGOUT_REQUEST"] = "APP_LOGOUT_REQUEST";
  WSEventType2["APP_GO_TO_ADMIN"] = "APP_GO_TO_ADMIN";
  WSEventType2["APP_GO_TO_ORG"] = "APP_GO_TO_ORG";
  WSEventType2["APP_CUSTOM_EVENT"] = "APP_CUSTOM_EVENT";
  return WSEventType2;
})(WSEventType || {});

// src/core.ts
function isBridgeMessage(msg) {
  return msg && (msg.namespace === "WS_BRIDGE" || msg.namespace === "ORG_BRIDGE") && typeof msg.type === "string" && ALLOWED_BRIDGE_VERSIONS.includes(msg.version) && typeof msg.from === "string" && ["workspace", "organization", "app", "admin"].includes(msg.from) && (msg.app_id === void 0 || typeof msg.app_id === "number");
}

// src/app.ts
function useAppBridge(options) {
  const {
    appId,
    appVersion,
    userId,
    workspaceId,
    orgId,
    debug = false,
    onMessage,
    onContextUpdate,
    onHello,
    onLanguageChanged,
    onWorkspaceChanged,
    onSessionExpired,
    onAppNavigate,
    onCustomEvent
  } = options;
  const [context, setContext] = useState(null);
  useEffect(() => {
    if (!debug) return;
    console.log(
      "[useAppBridge] \u{1F50D} Debug mode enabled - listening to ALL postMessage events"
    );
    const debugHandler = (event) => {
      console.log("[useAppBridge] \u{1F534} Raw postMessage received:", {
        origin: event.origin,
        data: event.data
      });
    };
    window.addEventListener("message", debugHandler);
    return () => window.removeEventListener("message", debugHandler);
  }, [debug]);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const handler = (event) => {
      const data = event.data;
      if (!isBridgeMessage(data)) return;
      if (data.from !== "workspace" && data.from !== "organization") return;
      if (data.type === "WS_HELLO" /* WS_HELLO */ || data.type === "ORG_HELLO" /* ORG_HELLO */) {
        setContext(data.payload);
        onHello?.(data.payload);
      }
      if (data.type === "WS_CONTEXT_UPDATE" /* WS_CONTEXT_UPDATE */ || data.type === "ORG_CONTEXT_UPDATE" /* ORG_CONTEXT_UPDATE */) {
        setContext(data.payload);
        onContextUpdate?.(data.payload);
      }
      if (data.type === "WS_LANGUAGE_CHANGED" /* WS_LANGUAGE_CHANGED */ || data.type === "ORG_LANGUAGE_CHANGED" /* ORG_LANGUAGE_CHANGED */) {
        onLanguageChanged?.(data.payload?.locale);
      }
      if (data.type === "WS_WORKSPACE_CHANGED" /* WS_WORKSPACE_CHANGED */ || data.type === "ORG_WORKSPACE_CHANGED" /* ORG_WORKSPACE_CHANGED */) {
        onWorkspaceChanged?.(
          data.payload?.workspace_id,
          data.payload?.workspace_name
        );
      }
      if (data.type === "WS_SESSION_EXPIRED" /* WS_SESSION_EXPIRED */ || data.type === "ORG_SESSION_EXPIRED" /* ORG_SESSION_EXPIRED */) {
        onSessionExpired?.();
      }
      if (data.type === "WS_APP_NAVIGATE" /* WS_APP_NAVIGATE */ || data.type === "ORG_APP_NAVIGATE" /* ORG_APP_NAVIGATE */) {
        onAppNavigate?.(data.payload?.route, data.payload?.params);
      }
      if (data.type === "WS_CUSTOM_EVENT" /* WS_CUSTOM_EVENT */ || data.type === "ORG_CUSTOM_EVENT" /* ORG_CUSTOM_EVENT */) {
        onCustomEvent?.(data.payload);
      }
      onMessage?.(data, event);
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, [
    onMessage,
    onContextUpdate,
    onHello,
    onLanguageChanged,
    onWorkspaceChanged,
    onSessionExpired,
    onAppNavigate,
    onCustomEvent
  ]);
  const postToParent = useCallback(
    (type, payload) => {
      if (typeof window === "undefined" || !window.parent) return;
      const msg = {
        namespace: "WS_BRIDGE",
        from: "app",
        app_id: appId,
        type,
        version: BRIDGE_VERSION,
        payload
      };
      window.parent.postMessage(msg, "*");
    },
    [appId]
  );
  const sendHello = useCallback(() => {
    postToParent("APP_HELLO" /* APP_HELLO */, {
      app_id: appId,
      app_version: appVersion,
      user_id: userId,
      workspace_id: workspaceId,
      org_id: orgId
    });
  }, [postToParent, appId, appVersion, userId, workspaceId, orgId]);
  const sendReady = useCallback(
    (initialRoute, metadata) => {
      postToParent("APP_READY" /* APP_READY */, {
        workspace_id: context?.workspace_id,
        initial_route: initialRoute,
        metadata
      });
    },
    [postToParent, context]
  );
  const requestContext = useCallback(() => {
    postToParent("APP_CONTEXT_REQUEST" /* APP_CONTEXT_REQUEST */, {
      workspace_id: context?.workspace_id
    });
  }, [postToParent, context]);
  const showToast = useCallback(
    (payload) => {
      postToParent("APP_SHOW_TOAST" /* APP_SHOW_TOAST */, payload);
    },
    [postToParent]
  );
  const navigate = useCallback(
    (payload) => {
      postToParent("APP_NAVIGATE" /* APP_NAVIGATE */, payload);
    },
    [postToParent]
  );
  const sendCustomEvent = useCallback(
    (payload) => {
      postToParent("APP_CUSTOM_EVENT" /* APP_CUSTOM_EVENT */, payload);
    },
    [postToParent]
  );
  return {
    context,
    postToParent,
    sendHello,
    sendReady,
    requestContext,
    showToast,
    navigate,
    sendCustomEvent
  };
}

// src/host.ts
import { useCallback as useCallback2, useEffect as useEffect2 } from "react";
function useParentBridge(options) {
  const {
    getIframeWindowByAppId,
    getIframeOriginByAppId,
    parentType = "workspace" /* WORKSPACE */,
    debug = false,
    onMessage
  } = options;
  useEffect2(() => {
    if (!debug) return;
    console.log(
      "[useParentBridge] \u{1F50D} Debug mode enabled - listening to ALL postMessage events"
    );
    const debugHandler = (event) => {
      console.log("[useParentBridge] \u{1F534} Raw postMessage received:", {
        origin: event.origin,
        data: event.data
      });
    };
    window.addEventListener("message", debugHandler);
    return () => window.removeEventListener("message", debugHandler);
  }, [debug]);
  useEffect2(() => {
    if (typeof window === "undefined") return;
    const handler = (event) => {
      const data = event.data;
      if (!isBridgeMessage(data)) return;
      onMessage?.(data, event);
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, [onMessage]);
  const postToApp = useCallback2(
    (appId, type, payload) => {
      const win = getIframeWindowByAppId(appId);
      const origin = getIframeOriginByAppId(appId) ?? "*";
      if (!win) return;
      const msg = {
        namespace: "WS_BRIDGE",
        from: parentType,
        app_id: appId,
        type,
        version: BRIDGE_VERSION,
        payload
      };
      win.postMessage(msg, origin);
    },
    [getIframeWindowByAppId, getIframeOriginByAppId, parentType]
  );
  const sendHello = useCallback2(
    (appId, context) => {
      const eventType = parentType === "organization" /* ORGANIZATION */ ? "ORG_HELLO" /* ORG_HELLO */ : "WS_HELLO" /* WS_HELLO */;
      postToApp(appId, eventType, context);
    },
    [postToApp, parentType]
  );
  const sendContextUpdate = useCallback2(
    (appId, context) => {
      const eventType = parentType === "organization" /* ORGANIZATION */ ? "ORG_CONTEXT_UPDATE" /* ORG_CONTEXT_UPDATE */ : "WS_CONTEXT_UPDATE" /* WS_CONTEXT_UPDATE */;
      postToApp(appId, eventType, context);
    },
    [postToApp, parentType]
  );
  const navigateApp = useCallback2(
    (appId, route, params) => {
      const eventType = parentType === "organization" /* ORGANIZATION */ ? "ORG_APP_NAVIGATE" /* ORG_APP_NAVIGATE */ : "WS_APP_NAVIGATE" /* WS_APP_NAVIGATE */;
      postToApp(appId, eventType, { route, params });
    },
    [postToApp, parentType]
  );
  return {
    postToApp,
    sendHello,
    sendContextUpdate,
    navigateApp
  };
}
var useWorkspaceBridge = useParentBridge;
var useOrganizationBridge = useParentBridge;

// src/components/IframeBridge.tsx
import React, {
  forwardRef,
  useEffect as useEffect3,
  useImperativeHandle,
  useRef,
  useState as useState2
} from "react";
var IframeBridge = forwardRef(
  ({
    url,
    appId,
    parentType = "workspace" /* WORKSPACE */,
    context,
    origin = "*",
    className = "w-full h-full border-0",
    onIframeLoad,
    onWSHello,
    onOrgHello,
    onContextUpdate,
    onAppHello,
    onAppReady,
    onAppContextRequest,
    onValidateUser,
    onAppShowToast,
    onAppNavigate,
    onAppLogoutRequest,
    onAppGoToAdmin,
    onAppGoToOrg,
    onAppCustomEvent,
    onMessage,
    showLoading = false,
    loadingComponent,
    onError,
    onTimeout,
    debug = false,
    allow = ""
  }, ref) => {
    const iframeRef = useRef(null);
    const [hasReceivedAppReady, setHasReceivedAppReady] = useState2(false);
    const [isLoading, setIsLoading] = useState2(true);
    useEffect3(() => {
      if (!debug) return;
      console.log(
        "[IframeBridge] \u{1F50D} Debug mode enabled - listening to ALL postMessage events"
      );
      const debugHandler = (event) => {
        console.log("[IframeBridge] \u{1F534} Raw postMessage received:", {
          origin: event.origin,
          data: event.data
        });
      };
      window.addEventListener("message", debugHandler);
      return () => window.removeEventListener("message", debugHandler);
    }, [debug]);
    useEffect3(() => {
      const timeout = setTimeout(() => {
        if (!hasReceivedAppReady) {
          const error = new Error("App load timeout (30s)");
          if (debug) {
            console.error("[IframeBridge]", error);
          }
          onError?.(error);
          onTimeout?.();
        }
      }, 3e4);
      return () => clearTimeout(timeout);
    }, [hasReceivedAppReady, onError, onTimeout, debug]);
    const { postToApp, sendHello, sendContextUpdate, navigateApp } = useParentBridge({
      parentType,
      debug,
      getIframeWindowByAppId: (id) => {
        if (id !== appId) return null;
        return iframeRef.current?.contentWindow ?? null;
      },
      getIframeOriginByAppId: (id) => {
        if (id !== appId) return null;
        return origin;
      },
      onMessage: async (msg, event) => {
        if (debug) {
          console.log("[IframeBridge] Received:", msg);
        }
        onMessage?.(msg, event);
        if (debug) {
          console.log(
            "[IframeBridge] handle message:",
            msg.from === "app" && msg.app_id === appId
          );
        }
        if (msg.from === "app" && msg.app_id === appId) {
          switch (msg.type) {
            case "APP_READY" /* APP_READY */:
              if (debug) {
                console.log("[IframeBridge] APP_READY", msg);
              }
              onAppReady?.(msg.payload);
              setHasReceivedAppReady(true);
              setIsLoading(false);
              break;
            case "APP_HELLO" /* APP_HELLO */:
              if (debug) {
                console.log("[IframeBridge] APP_HELLO", msg);
              }
              const helloPayload = msg.payload;
              onAppHello?.(helloPayload);
              if (onValidateUser) {
                const isValid = await Promise.resolve(
                  onValidateUser(helloPayload)
                );
                if (!isValid) {
                  if (debug) {
                    console.warn(
                      "[IframeBridge] User validation failed for APP_HELLO:",
                      helloPayload
                    );
                  }
                  onError?.(new Error("User validation failed"));
                  break;
                }
              }
              if (context) {
                if (debug) {
                  console.log(
                    "[IframeBridge] Sending HELLO + context after APP_HELLO"
                  );
                }
                sendHello(appId, context);
                if (parentType === "workspace" /* WORKSPACE */) {
                  onWSHello?.(context);
                }
                if (parentType === "organization" /* ORGANIZATION */) {
                  onOrgHello?.(context);
                }
                sendContextUpdate(appId, context);
                onContextUpdate?.(context);
              }
              break;
            case "APP_CONTEXT_REQUEST" /* APP_CONTEXT_REQUEST */:
              if (debug) {
                console.log("[IframeBridge] APP_CONTEXT_REQUEST", msg);
              }
              onAppContextRequest?.(msg.payload);
              if (context) {
                sendContextUpdate(appId, context);
              }
              break;
            case "APP_SHOW_TOAST" /* APP_SHOW_TOAST */:
              if (debug) {
                console.log("[IframeBridge] APP_SHOW_TOAST", msg);
              }
              onAppShowToast?.(msg.payload);
              break;
            case "APP_NAVIGATE" /* APP_NAVIGATE */:
              if (debug) {
                console.log("[IframeBridge] APP_NAVIGATE", msg);
              }
              onAppNavigate?.(msg.payload);
              break;
            case "APP_LOGOUT_REQUEST" /* APP_LOGOUT_REQUEST */:
              if (debug) {
                console.log("[IframeBridge] APP_LOGOUT_REQUEST", msg);
              }
              onAppLogoutRequest?.();
              break;
            case "APP_GO_TO_ADMIN" /* APP_GO_TO_ADMIN */:
              if (debug) {
                console.log("[IframeBridge] APP_GO_TO_ADMIN", msg);
              }
              onAppGoToAdmin?.();
              break;
            case "APP_GO_TO_ORG" /* APP_GO_TO_ORG */:
              if (debug) {
                console.log("[IframeBridge] APP_GO_TO_ORG", msg);
              }
              onAppGoToOrg?.();
              break;
            case "APP_CUSTOM_EVENT" /* APP_CUSTOM_EVENT */:
              if (debug) {
                console.log("[IframeBridge] APP_CUSTOM_EVENT", msg);
              }
              onAppCustomEvent?.(msg.payload);
              break;
            default:
              if (debug) {
                console.warn("[IframeBridge] Unknown event type:", msg.type);
              }
          }
        }
      }
    });
    const postToAppWithRetry = async (type, payload, retries = 3) => {
      for (let i = 0; i < retries; i++) {
        try {
          postToApp(appId, type, payload);
          if (debug) {
            console.log(`[IframeBridge] Sent ${type}:`, payload);
          }
          return;
        } catch (error) {
          if (debug) {
            console.error(
              `[IframeBridge] Failed to send ${type} (attempt ${i + 1}/${retries}):`,
              error
            );
          }
          if (i === retries - 1) {
            onError?.(error);
            throw error;
          }
          await new Promise((resolve) => setTimeout(resolve, 1e3 * (i + 1)));
        }
      }
    };
    useImperativeHandle(ref, () => ({
      sendHello: (ctx) => {
        sendHello(appId, ctx);
        if (parentType === "workspace" /* WORKSPACE */) {
          onWSHello?.(ctx);
        }
        if (parentType === "organization" /* ORGANIZATION */) {
          onOrgHello?.(ctx);
        }
      },
      sendContextUpdate: (ctx) => {
        sendContextUpdate(appId, ctx);
        onContextUpdate?.(ctx);
      },
      sendLanguageChanged: (locale) => {
        const eventType = parentType === "organization" /* ORGANIZATION */ ? "ORG_LANGUAGE_CHANGED" /* ORG_LANGUAGE_CHANGED */ : "WS_LANGUAGE_CHANGED" /* WS_LANGUAGE_CHANGED */;
        postToApp(appId, eventType, { locale });
      },
      sendWorkspaceChanged: (workspaceId, workspaceName) => {
        const eventType = parentType === "organization" /* ORGANIZATION */ ? "ORG_WORKSPACE_CHANGED" /* ORG_WORKSPACE_CHANGED */ : "WS_WORKSPACE_CHANGED" /* WS_WORKSPACE_CHANGED */;
        postToApp(appId, eventType, {
          workspace_id: workspaceId,
          workspace_name: workspaceName
        });
      },
      sendOrgChanged: (orgId, orgName, workspaceId, workspaceName) => {
        const eventType = parentType === "organization" /* ORGANIZATION */ ? "ORG_WORKSPACE_CHANGED" /* ORG_WORKSPACE_CHANGED */ : "WS_ORG_CHANGED" /* WS_ORG_CHANGED */;
        postToApp(appId, eventType, {
          org_id: orgId,
          org_name: orgName,
          workspace_id: workspaceId,
          workspace_name: workspaceName
        });
      },
      sendSessionExpired: () => {
        const eventType = parentType === "organization" /* ORGANIZATION */ ? "ORG_SESSION_EXPIRED" /* ORG_SESSION_EXPIRED */ : "WS_SESSION_EXPIRED" /* WS_SESSION_EXPIRED */;
        postToApp(appId, eventType, {});
      },
      navigateApp: (route, params) => {
        navigateApp(appId, route, params);
      },
      sendCustomEvent: (eventName, data) => {
        const eventType = parentType === "organization" /* ORGANIZATION */ ? "ORG_CUSTOM_EVENT" /* ORG_CUSTOM_EVENT */ : "WS_CUSTOM_EVENT" /* WS_CUSTOM_EVENT */;
        postToApp(appId, eventType, {
          event_name: eventName,
          data
        });
      },
      postToApp: (type, payload) => {
        postToAppWithRetry(type, payload);
      }
    }));
    const handleIframeLoad = () => {
      onIframeLoad?.();
      if (debug) {
        console.log("[IframeBridge] Iframe loaded, waiting for APP_HELLO...");
      }
    };
    useEffect3(() => {
      if (context && iframeRef.current?.contentWindow) {
        sendContextUpdate(appId, context);
        if (debug) {
          console.log("[IframeBridge] Context updated:", context);
        }
      }
    }, [JSON.stringify(context), appId, sendContextUpdate, debug]);
    return /* @__PURE__ */ React.createElement(React.Fragment, null, showLoading && isLoading && loadingComponent, /* @__PURE__ */ React.createElement(
      "iframe",
      {
        ref: iframeRef,
        src: url,
        onLoad: handleIframeLoad,
        className,
        title: `App ${appId}`,
        allow
      }
    ));
  }
);
IframeBridge.displayName = "IframeBridge";
export {
  ALLOWED_BRIDGE_VERSIONS,
  BRIDGE_VERSION,
  IframeBridge,
  ParentType,
  WSEventType,
  isBridgeMessage,
  useAppBridge,
  useOrganizationBridge,
  useParentBridge,
  useWorkspaceBridge
};
//# sourceMappingURL=ws-bridge.js.map
